/**
 * Display helpers shared by the remittance screens, the header bell and the
 * dashboard banner.
 */

import * as dates from './dateFormat';

import type {
  RemittanceAggregator,
  RemittanceChannel,
  RemittanceTrackingStatus,
} from '../services/remittances';

export const AGGREGATOR_LOGOS: Record<RemittanceAggregator, string> = {
  FICO_PAY: '/images/logo_fico.png',
  MOOV_MONEY: '/images/logo_moov_money.png',
  KONOOM: '/images/konoom_logo.png',
  AIRTEL_MONEY: '/images/logo_airtel_money.png',
};

/** Short label of a channel, used where the aggregator is already shown. */
export const CHANNEL_ORDER: RemittanceChannel[] = [
  'ORANGE_MONEY',
  'MTN_MOBILE_MONEY',
  'FICO_COUNTER',
  'MOOV_MONEY',
  'KONOOM',
  'AIRTEL_MONEY',
];

/**
 * Series colours of the evolution chart: categorical slots 1-3 of the
 * validated data-viz palette, in fixed order (collected, remitted, balance),
 * stepped separately for the dark surface. Validated with the palette checker
 * (light: aqua below 3:1 on the surface, hence the table view under the chart).
 */
export function chartSeriesColors(mode: 'light' | 'dark') {
  return mode === 'dark'
    ? { collected: '#3987e5', remitted: '#d95926', balance: '#199e70' }
    : { collected: '#2a78d6', remitted: '#eb6834', balance: '#1baf7a' };
}

type ChipColor = 'success' | 'info' | 'warning' | 'error' | 'secondary';

export const TRACKING_STATUS_CHIP: Record<RemittanceTrackingStatus, ChipColor> = {
  UP_TO_DATE: 'success',
  TO_REMIT: 'info',
  LATE: 'warning',
  CRITICAL: 'error',
  OVER_REMITTED: 'secondary',
};

/** Amount with thousand separators; XAF has no cents, decimals only when present. */
export function formatMoney(value: number | null | undefined, currency = 'XAF'): string {
  const amount = Number(value ?? 0);
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(amount)} ${currency}`;
}

/** YYYY-MM-DD shown in the user's language, parsed as a local day (no UTC shift). */
export function formatDay(value: string | null | undefined, language?: string): string {
  return dates.formatDate(value?.slice(0, 10), dates.DATE_MEDIUM_FORMAT, '-', language);
}

export function formatDateTime(value: string | null | undefined): string {
  return dates.formatDateTime(value, dates.DATE_TIME_MEDIUM_FORMAT);
}

function toIsoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function todayIso(): string {
  return toIsoDay(new Date());
}

export function firstDayOfMonthIso(): string {
  const now = new Date();
  return toIsoDay(new Date(now.getFullYear(), now.getMonth(), 1));
}

export function formatRate(value: number | null | undefined): string {
  return value === null || value === undefined ? '-' : `${value.toFixed(1)} %`;
}
